import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, X, Star, Download } from 'lucide-react';
import { RatingInput } from '../components/RatingBadge';
import { ratingLabel } from '../utils/rating10';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyBookingsPage = () => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingData, setRatingData] = useState({ booking_id: '', rating: 9, comment: '' });
  const [showRatingForm, setShowRatingForm] = useState(false);

  const fetchBookings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/guest/bookings`, { withCredentials: true });
      setBookings(data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleCancelBooking = async (bookingId) => {
    if (!confirm('Are you sure you want to cancel this booking?')) return;

    try {
      await axios.put(`${API}/guest/bookings/${bookingId}/cancel`, {}, { withCredentials: true });
      toast.success('Booking cancelled successfully');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleRateHotel = (bookingId) => {
    setRatingData({ booking_id: bookingId, rating: 9, comment: '' });
    setShowRatingForm(true);
  };

  const submitRating = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/guest/ratings`, ratingData, { withCredentials: true });
      toast.success('Rating submitted successfully');
      setShowRatingForm(false);
      setRatingData({ booking_id: '', rating: 9, comment: '' });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };

  const downloadVoucher = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/guest/bookings/${bookingId}/voucher`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `localliink-voucher-${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Voucher downloaded');
    } catch (error) {
      toast.error('Failed to download voucher');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF0059]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-slate-50" data-testid="my-bookings-page">
      <div className="max-w-6xl mx-auto px-6">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">{t('mybookings.title')}</h1>
        <p className="text-gray-600 mb-8">{t('mybookings.subtitle')}</p>

        {bookings.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="no-bookings-message">
            <p className="text-slate-600">You have no bookings yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div
                key={booking._id}
                className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`booking-${booking._id}`}
              >
                <div className="flex flex-col md:flex-row gap-4">
                  {booking.hotel_image && (
                    <div className="md:w-48 h-32 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                      <img src={booking.hotel_image} alt={booking.hotel_name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{booking.hotel_name || 'Hotel'}</h3>
                        {booking.hotel_city && (
                          <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3" /> {booking.hotel_city}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">Booking ID: {booking._id}</p>
                      </div>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          booking.status === 'confirmed'
                            ? 'bg-green-100 text-green-700'
                            : booking.status === 'checked_out'
                            ? 'bg-[#FFE5EE] text-[#FF0059]'
                            : booking.status === 'cancelled'
                            ? 'bg-red-100 text-red-700'
                            : booking.status === 'no_show'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                        data-testid={`booking-status-${booking._id}`}
                      >
                        {booking.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                      <div>
                        <p className="text-slate-500 text-xs">Check-in</p>
                        <p className="font-semibold text-slate-900 flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-[#FF0059]" /> {booking.check_in}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Check-out</p>
                        <p className="font-semibold text-slate-900 flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-[#FF0059]" /> {booking.check_out}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Guests</p>
                        <p className="font-semibold text-slate-900">{booking.guests}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Payment</p>
                        <p className="font-semibold text-slate-900">Pay at Hotel</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadVoucher(booking._id)}
                        className="border-[#FF0059] text-[#FF0059] hover:bg-[#FFE5EE]"
                        data-testid={`download-voucher-${booking._id}`}
                      >
                        <Download className="h-3 w-3 mr-1" /> Voucher PDF
                      </Button>
                      {booking.status === 'confirmed' && booking.can_cancel && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleCancelBooking(booking._id)}
                          data-testid={`cancel-booking-${booking._id}`}
                        >
                          Cancel Booking
                        </Button>
                      )}
                      {(booking.status === 'confirmed' || booking.status === 'no_show' || booking.status === 'checked_out') && !booking.already_rated && (
                        <Button
                          size="sm"
                          onClick={() => handleRateHotel(booking._id)}
                          className="bg-[#FF0059] hover:bg-[#CC0047]"
                          data-testid={`rate-hotel-${booking._id}`}
                        >
                          <Star className="h-3 w-3 mr-1" /> Rate Hotel
                        </Button>
                      )}
                      {booking.already_rated && (
                        <span className="inline-flex items-center text-xs font-semibold text-green-600 px-3 py-2" data-testid={`already-rated-${booking._id}`}>
                          <Star className="h-3 w-3 mr-1 fill-green-600" /> You rated this hotel
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Rating Modal */}
        {showRatingForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="rating-modal">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Rate Your Stay</h3>
                <button onClick={() => setShowRatingForm(false)} data-testid="close-rating-modal">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={submitRating} className="space-y-4" data-testid="rating-form">
                <div>
                  <Label>Your Rating · {ratingData.rating}/10 · {ratingLabel(ratingData.rating)}</Label>
                  <div className="mt-2" data-testid="rating-stars">
                    <RatingInput value={ratingData.rating} onChange={(v) => setRatingData({ ...ratingData, rating: v })} testIdPrefix="rate-num" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="comment">Comment (optional)</Label>
                  <textarea
                    id="comment"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md mt-1"
                    rows="3"
                    placeholder="Share your experience..."
                    value={ratingData.comment}
                    onChange={(e) => setRatingData({ ...ratingData, comment: e.target.value })}
                    data-testid="comment-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#FF0059] hover:bg-[#CC0047]" data-testid="submit-rating">
                  Submit Rating
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
